import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, IconButton, TextField } from "@mui/material";
import apiClient from "../services/apiClient";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { MdDelete, MdFilterList, MdFilterListOff } from "react-icons/md";
import { RiEdit2Fill } from "react-icons/ri";

import SearchIcon from "@mui/icons-material/Search";
import PageLoader from "../components/Loading/PageLoader";
import CustomButton from "../components/Button/CustomButton";
import CustomInputBox from "../components/Form/CustomInputBox";
// import AddRequestPopUp from "../components/PopUpModal/AddRequestPopUp";
import { useDispatch, useSelector } from "react-redux";
import { setModal } from "../features/modal/ModalSlice";
import { PagesOutlined } from "@mui/icons-material";

const NumberAccess = () => {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState({
    search: "",
    status: "",
    page: 1,
    pageSize: 10,
  });

  const [loading, setLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(true);

  const userInfo = useSelector((state) => state?.userInfo?.user);
  const modal = useSelector((state) => state?.modal?.modal);

  useEffect(() => {
    fetchRequests();
  }, [filter]);

  useEffect(() => {
    if (modal?.isUpdated?.isUpdatedIn === "UPDATE_REQUEST") handleUpdate(modal?.isUpdated?.data);
    if (modal?.isUpdated?.isUpdatedIn === "NEW_REQUEST") handleNew(modal?.isUpdated?.data);
  }, [modal?.isUpdated]);

  const dispatch = useDispatch();

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(
        `/requests/user?owner_id=${userInfo?.id}&page=${filter?.page}&pageSize=${filter?.pageSize}&request_status=${filter?.status}`
      );
      setRequests(response?.data || []);
    } catch (error) {
      console.error("Error fetching requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleShowFilter = () => {
    setShowFilter(!showFilter);
  };

  const handleAddRequest = () => {
    dispatch(setModal({ type: "ADD_REQUESTS" }));
  };

  const handleEditRequest = (request) => {
    dispatch(setModal({ type: "EDIT_REQUESTS", data: request }));
  };

  const handleUpdate = (data) => {
    fetchRequests();
  };

  const handleNew = (data) => {
    fetchRequests();
  };

  const handleResetFilter = (e) => {
    setFilter({
      search: "",
      status: "",
      page: 1,
      pageSize: 10,
    });
  };

  const columns = useMemo(
    () => [
      { accessorKey: "no_of_phones_required", header: "Phones Required" },
      { accessorKey: "no_extensions_required", header: "Extensions Required" },
      {
        accessorKey: "assigned_phone_details",
        header: "Extensions Allocated",
        Cell: ({ cell }) => {
          const assigned_phone = cell.getValue();
          if (!assigned_phone) return "";
          const usernames = assigned_phone.map((phone) => phone.username).join(", ");
          return usernames;
        },
      },
      { accessorKey: "request_status", header: "Status" },
      { accessorKey: "created_at", header: "Date" },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: requests,
    enableColumnOrdering: true,
    enableRowSelection: true,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableRowActions: true,
    enablePagination: false,
    positionActionsColumn: "last",
    renderRowActions: ({ row }) => (
      <Box sx={{ display: "flex", flexWrap: "nowrap", gap: "8px" }}>
        <IconButton title="Edit Request" onClick={() => handleEditRequest(row?.original)}>
          <RiEdit2Fill />
        </IconButton>
        <IconButton disabled>
          <MdDelete />
        </IconButton>
      </Box>
    ),
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: "#F5F6F8",
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: "12px",
        border: "1px solid #e0e0e0",
      },
    },
  });

  return (
    <>
      {loading && <PageLoader text="Loading Requests" />}

      <div className="pageTitle1 px-5 py-4">
        <h3>Requests</h3>
        <p>{requests?.length} Available</p>
      </div>

      <Box sx={{ padding: 2 }}>
        <div className="d-flex justify-content-between">
          <div className="w-50">
            <CustomInputBox
              icon={SearchIcon}
              placeholder="Search Request"
              type="text"
              backgroundColor={"#FFFFFF"}
              borderRadius="8px"
            />
          </div>

          <div className="d-flex justify-content-end gap-3 mb-4">
            <CustomButton
              text={(showFilter ? "Hide" : "Show") + " Filter"}
              iconPosition="left"
              icon={showFilter ? <MdFilterListOff /> : <MdFilterList />}
              onClick={handleShowFilter}
              sx={{ 
                backgroundColor: "#FFFFFF",
                color: "#000000",
                borderRadius: "8px",
                padding: "8px 16px",
                border: "1px solid #d9d9d9",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#f7f6ffff",
                  boxShadow: "none",
                }
               }}
            />

            <CustomButton text={"Add Request"} onClick={handleAddRequest}
              sx={{ 
                backgroundColor: "#6859f7ff",
                color: "#FFFFFF",
                borderRadius: "8px",
                padding: "8px 16px",
                border: "1px solid #6859f7ff",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                }
               }}
            />
          </div>
        </div>

        {showFilter && (
          <div className="d-flex justify-content-start gap-3 mb-4">
            <Autocomplete
              disablePortal
              options={[]}
              size="small"
              renderInput={(params) => <TextField 
                sx={{ width: 200, backgroundColor: "white", borderRadius: "8px" }}
                {...params} label="Name" />}
            />

            <Autocomplete
              disablePortal
              options={["Pending", "Approved"]}
              value={filter?.status}
              name="status"
              onChange={(event, newValue) => setFilter((prev) => ({ ...prev, status: newValue }))}
              size="small"
              renderInput={(params) => <TextField 
                sx={{ width: 200, backgroundColor: "white", borderRadius: "8px" }}
                {...params} label="Status" />}
            />

            <CustomButton
              text={"Reset"}
              variant="text"
              onClick={handleResetFilter}
              sx={{ 
                backgroundColor: "#FFFFFF",
                color: "#000000",
                borderRadius: "8px",
                padding: "8px 16px",
                border: "1px solid #d9d9d9",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#f7f6ffff",
                  boxShadow: "none",
                }
               }}
            />
          </div>
        )}

        <MaterialReactTable table={table} />

        {/* <AddRequestPopUp
          show={showPopup}
          onHide={() => setShowPopup(false)}
          onSave={handleSaveRequest}
          request={selectedRequest}
        /> */}
      </Box>
    </>
  );
};

export default NumberAccess;
